import React, { useState, useEffect } from 'react'
import { ImageStyled } from '../assets/styles'
import {
	UPDATE_QUESTION_ACTIONS,
	UpdateQuestionAction,
} from '../model/editableQuestion/actions'
import { Dropdown, StudentResponse } from '../model/general'
import {
	ClientQuestion,
	ClientFields,
	variantTypes,
	ClientFields as SharedQuestionFields,
	FreeResponse,
	MultipleChoice,
	QuestionChoice,
	MultipleSelect,
	DropdownAnswer,
	FreeResponseAnswer,
	MultipleChoiceAnswer,
	MultipleSelectAnswer,
	ClozeDropdown,
	ImageLabeling,
	StudentAnswer,
	getImageDropdownKey,
	MediaObj,
} from '@mission.io/question-toolkit'
import { FaTimes, FaCheck } from 'react-icons/fa'
import ImageModal from '../components/image-modal/ImageModal'

/**
 * Returns styled image if exists or returns null. It is used for Choices Images or Question Images.
 * This image also has the option expandable a fullscreen Image
 * @param {MediaObj[]} media
 * @param {string} classname
 * @param {boolean} expandable
 * @param {MutableRefObject<HTMLElement>} parentRef
 * @returns {JSX.Element | null}
 */

function ImageWithRef(
	{
		media,
		expandable,
		className = '',
		parentRef,
	}: {
		media?: MediaObj[]
		className?: string
		expandable?: boolean
		parentRef?: React.MutableRefObject<HTMLElement | undefined>
	},
	ref: React.ForwardedRef<HTMLImageElement | null>
): JSX.Element | null {
	const [imageModalIsOpen, setImageModalIsOpen] = useState(false)
	// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	const imageSrc = media && media.length > 0 ? media[0].url : ''

	const handleShowImage = () => {
		if (imageSrc) setImageModalIsOpen(true)
	}

	if (!imageSrc) return null
	else
		return (
			<>
				{expandable && imageModalIsOpen && (
					<ImageModal
						isOpen={imageModalIsOpen}
						setClose={() => setImageModalIsOpen(false)}
						url={imageSrc}
						parentSelector={parentRef?.current}
						fullScreen
					/>
				)}
				<ImageStyled
					$clickable={expandable}
					className={className}
					alt="Image"
					src={imageSrc}
					ref={ref}
					onClick={expandable ? handleShowImage : undefined}
				/>
			</>
		)
}

export const Image = React.forwardRef(ImageWithRef)

export const isFreeResponseAnswer = (
	x: StudentAnswer
): x is FreeResponseAnswer => x.type === variantTypes.FREE_RESPONSE && true

/**
 * Returns flag if type of param object is type MultipleChoiceAnswer
 * @param {x: StudentAnswer } props
 * @returns {boolean}
 */
export const isMultipleChoiceAnswer = (
	x: StudentAnswer
): x is MultipleChoiceAnswer => x.type === variantTypes.MULTIPLE_CHOICE && true

/**
 * Returns flag if type of param object is type MultipleSelectAnswer
 * @param {x: StudentAnswer} props
 * @returns {boolean}
 */
export const isMultipleSelectAnswer = (
	x: StudentAnswer
): x is MultipleSelectAnswer => x.type === variantTypes.MULTIPLE_SELECT && true

/**
 * Returns flag if type of param object is type DropdownAnswer
 * @param {x: StudentAnswer} props
 * @returns {boolean}
 */
export const isDropdownAnswer = (x: StudentAnswer): x is DropdownAnswer => {
	return (
		(x.type === variantTypes.IMAGE_LABELING ||
			x.type === variantTypes.CLOZE_DROPDOWN) &&
		true
	)
}

/**
 * Returns flag if type of param object is type FreeResponse
 * @param {x: ClientQuestion } props
 * @returns {boolean}
 */
export const isFreeResponse = (
	x: ClientQuestion
): x is FreeResponse<SharedQuestionFields> =>
	x.type === variantTypes.FREE_RESPONSE && true

/**
 * Returns flag if type of param object is type MultipleChoice
 * @param {x: ClientQuestion } props
 * @returns {boolean}
 */
export const isMultipleChoice = (
	x: ClientQuestion
): x is MultipleChoice<SharedQuestionFields> =>
	x.type === variantTypes.MULTIPLE_CHOICE && true

/**
 * Returns flag if type of param object is type MultipleSelect
 * @param {x: ClientQuestion} props
 * @returns {boolean}
 */
export const isMultipleSelect = (
	x: ClientQuestion
): x is MultipleSelect<SharedQuestionFields> =>
	x.type === variantTypes.MULTIPLE_SELECT && true

/**
 * Returns flag if type of param object is type ClozeDropdown
 * @param {x: ClientQuestion} props
 * @returns {boolean}
 */
export const isClozeDropdown = (
	x: ClientQuestion
): x is ClozeDropdown<SharedQuestionFields> => {
	return x.type === variantTypes.CLOZE_DROPDOWN && true
}

/**
 * Returns flag if type of param object is type ImageLabeling
 * @param {x: ClientQuestion} props
 * @returns {boolean}
 */
export const isImageLabeling = (
	x: ClientQuestion
): x is ImageLabeling<SharedQuestionFields> =>
	x.type === variantTypes.IMAGE_LABELING && true

export const ALPHABET: string[] = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')

/**
 * Takes the question index, and returns the correct preceding letter,
 * modding it if necessary
 * @param {number}    -question Index
 * @return {string}  - get the letter associate with number passed
 */
export function getQuestionOptionIndex(index: number): string {
	const alphabetArrayLength = ALPHABET.length
	// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	return ALPHABET[index % alphabetArrayLength]
}

/**
 * Takes the data from the question and makes the appropriate title to show for the modal.
 * @param {ClientQuestion} question -  the question
 * @param {StudentResponse} studentResponse - the studentResponse
 * @return {string}
 */
export function generateTitle(
	question: ClientQuestion,
	studentResponse?: StudentResponse | null
): string {
	const studentAnswer = studentResponse?.answer
	if (!studentAnswer) return 'Question - Did not answer'
	if (isFreeResponse(question) && isFreeResponseAnswer(studentAnswer)) {
		return `Question - ${
			!studentResponse.score || studentResponse.score.score === null
				? 'Ungraded'
				: studentResponse.score.score >= studentResponse.score.perfectScore
				? 'Correct'
				: 'Incorrect'
		}`
	} else if (
		isMultipleChoice(question) &&
		isMultipleChoiceAnswer(studentAnswer)
	) {
		const isCorrect = question.choices.find(
			item => String(item._id) === String(studentAnswer.value)
		)?.answer
		return `Question - ${isCorrect ? 'Correct' : 'Incorrect'}`
	} else if (isDropdownAnswer(studentAnswer)) {
		let amountCorrects = 0
		let total = 0

		if (isImageLabeling(question)) {
			question.dropdowns.forEach(dropdown => {
				const choice = dropdown.choices.find((c: QuestionChoice) => c.answer)
				if (
					studentAnswer.value[getImageDropdownKey(dropdown)] ===
					String(choice?._id)
				)
					amountCorrects += 1
				return choice
			})
			total = question.dropdowns.length
		} else if (isClozeDropdown(question)) {
			question.dropdowns.forEach(dropdown => {
				const choice = dropdown.choices.find((c: QuestionChoice) => c.answer)
				if (
					studentAnswer.value[dropdown.templateResponseKey] ===
					String(choice?._id)
				)
					amountCorrects += 1
				return choice
			})
			total = question.dropdowns.length
		}
		return `Question - ${amountCorrects}/${total} Correct`
	} else if (
		isMultipleSelect(question) &&
		isMultipleSelectAnswer(studentAnswer)
	) {
		const isInCorrect = question.choices.find(
			item =>
				(!item.answer && studentAnswer.value.indexOf(String(item._id)) > -1) ||
				(item.answer && studentAnswer.value.indexOf(String(item._id)) === -1)
		)
		return `Question - ${isInCorrect ? 'Incorrect' : 'Correct'}`
	} else return `Question - Correct`
}

export function randomString(): string {
	return Math.random()
		.toString(8)
		.replace('0.', '')
}

// Function takes single uploaded img file, and returns localUrl
export const getImageMeta = (file: Blob): { localUrl: string } => {
	const localUrl = URL.createObjectURL(file)

	return { localUrl }
}

/**
 * Given an image file determines the dimensions of the image.
 * @param {Blob} file
 * @returns {Promise<{width: number, height: number}} the width and height of the image
 */
export function getImageDimensions(
	file: Blob
): Promise<{ width: number; height: number }> {
	const reader = new FileReader()
	const image = new window.Image()

	const getWidthHeight = function(): { width: number; height: number } {
		const height = image.height
		const width = image.width
		return { height, width }
	}

	return new Promise((resolve, reject) => {
		reader.onload = (readerEvent: any) => {
			image.onload = () => resolve(getWidthHeight())
			image.onerror = reject
			image.src = readerEvent.target.result
		}
		reader.readAsDataURL(file)
	})
}

/**
 * Create an object with the main functions for Muliple Choice and Selecte.
 * @param { ?(update: UpdateQuestionAction) => void,} onChange
 * @param {boolean} readOnly - the  boolean readOnly
 * @return  { [key: string]: any }
 */
export function getChoiceHandlers(
	onChange?: (update: UpdateQuestionAction) => void,
	readOnly?: boolean
): { [key: string]: any } {
	const handleDeleteChoice = (index: number, dropdownIndex?: number) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.DELETE_CHOICE,
				payload: { index, dropdownIndex },
			})
		}
	}

	const handleDeleteImageChoice = (choiceIndex: number) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.DELETE_IMG_CHOICE,
				payload: { index: choiceIndex },
			})
		}
	}

	const handleAddChoice = (dropdownIndex?: number) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.ADD_CHOICE,
				payload: {
					choice: {
						text: '',
						answer: false,
						// @ts-expect-error  We need a different type for media in Editable Question
						media: [] as Media[],
					},
					dropdownIndex,
				},
			})
		}
	}

	const handleAnswerChange = (index: number, dropdownIndex?: number) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.CHANGE_ANSWER_CHOICE,
				payload: { index, dropdownIndex },
			})
		}
	}

	const handleChange = (
		index: number,
		value: string,
		dropdownIndex?: number
	) => {
		if (!readOnly && onChange) {
			onChange({
				type: UPDATE_QUESTION_ACTIONS.CHANGE_CHOICE,
				payload: { index, value, dropdownIndex },
			})
		}
	}

	const handleChangeChoiceImage = (file: File | Blob, index: number) => {
		if (!readOnly && onChange && index > -1) {
			const { localUrl } = getImageMeta(file)

			onChange({
				type: UPDATE_QUESTION_ACTIONS.CHANGE_MEDIA_CHOICE,
				payload: {
					index,
					media: {
						type: 'IMAGE',
						file: file,
						localUrl: localUrl,
					},
				},
			})
		}
	}
	return {
		handleDeleteChoice,
		handleDeleteImageChoice,
		handleAnswerChange,
		handleAddChoice,
		handleChange,
		handleChangeChoiceImage,
	}
}

/*
 * If the choice option is the correct one, show a green check mark icon,
 * else if the choice option selected by the student was not the correct one,
 * show a red close icon.  Else show nothing, because it was not right and not chosen.
 */
export function QuestionOptionIcon({
	correctOption,
	studentChoice,
}: {
	correctOption: boolean
	studentChoice: boolean
}): JSX.Element {
	let icon = null
	if (correctOption) {
		icon = <FaCheck aria-label="Correct Icon" />
	} else if (studentChoice) {
		icon = <FaTimes aria-label="Incorrect Icon" />
	} else icon = <div className="mx-2" />
	return <div className="mx-2">{icon}</div>
}

type Dimension = {
	height: number
	width: number
}

type ImageDimension = Dimension & {
	src: string
}

/**
 * Gets the current dimensions of the element in the given ref.
 * @param ref the reference to the element
 */
export function useDimensions(
	ref: React.MutableRefObject<HTMLElement | null>
): Dimension {
	const [dimensions, setDimensions] = useState<Dimension>(() => {
		if (!ref?.current) return { height: 0, width: 0 }

		return {
			height: ref.current.offsetHeight,
			width: ref.current.offsetWidth,
		}
	})

	useEffect(() => {
		const handleGetDimensions = () => {
			if (ref && ref.current) {
				setDimensions({
					height: ref.current.offsetHeight,
					width: ref.current.offsetWidth,
				})
			}
		}
		handleGetDimensions()
		window.addEventListener('resize', handleGetDimensions)
		return () => {
			window.removeEventListener('resize', handleGetDimensions)
		}
	}, [])
	return dimensions
}

/**
 * Returns the dimensions for an image element. Gets new dimensions when the image loads or the window resizes.
 * @param {React.MutableRefObject<HTMLImageElement | null>} element
 * @return  {ImageDimension?}
 */
export function useImageDimensions(
	element: React.MutableRefObject<HTMLImageElement | null> | null,
	src: string | undefined
): ImageDimension | null {
	const [dimensions, setDimensions] = useState<ImageDimension | null>(null)
	const [loaded, setLoaded] = useState(
		element?.current?.complete && element.current.naturalWidth !== 0
	)

	useEffect(() => {
		if (loaded) {
			return
		}
		const onImageLoaded = () => {
			setLoaded(true)
		}
		const imgElCurrent = element?.current
		if (imgElCurrent) {
			imgElCurrent.addEventListener('load', onImageLoaded)
			return () => imgElCurrent.removeEventListener('load', onImageLoaded)
		}
	}, [element, loaded])

	useEffect(() => {
		if (!loaded) return
		const handleGetDimensions = () => {
			if (element && element.current && src) {
				const el = element.current

				setDimensions({
					height: el.offsetHeight,
					width: el.offsetWidth,
					src,
				})
			}
		}
		handleGetDimensions()
		window.addEventListener('resize', handleGetDimensions)
		return () => {
			window.removeEventListener('resize', handleGetDimensions)
		}
	}, [loaded, src])
	return dimensions
}

export function isStudentDropdownAnswerCorrect(
	dropdown: Dropdown,
	key: string,
	studentAnswer?: DropdownAnswer
): boolean {
	const correctChoice = dropdown.choices.find((c: QuestionChoice) => c.answer)

	return String(correctChoice?._id) === String(studentAnswer?.value[key])
}

/**
 * Gets the score for a student answer of a dropdown question (Cloze Dropdown or Image Labeling)
 * @param studentAnswer the answer the student gave for the question
 * @param question the question the student answered
 * @returns { correct: number, total: number } the score in object form number of correct dropdowns and total dropdowns.
 */
export function getStudentDropdownScore(
	studentAnswer: StudentAnswer | undefined,
	question: ImageLabeling<ClientFields> | ClozeDropdown<ClientFields>
): { correct: number; total: number } | void {
	if (studentAnswer && isDropdownAnswer(studentAnswer)) {
		let auxCorrect = 0

		question.dropdowns.forEach((dropdown: Dropdown, index: number) => {
			const choice = dropdown.choices.find(
				(choice: QuestionChoice) => choice.answer
			)
			if (
				isImageLabeling(question) &&
				// @ts-expect-error TS2345 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				studentAnswer.value[getImageDropdownKey(question.dropdowns[index])] ===
					String(choice?._id)
			) {
				auxCorrect += 1
			} else if (
				isClozeDropdown(question) &&
				// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				studentAnswer.value[question.dropdowns[index].templateResponseKey] ===
					String(choice?._id)
			) {
				auxCorrect += 1
			}
		})
		return { correct: auxCorrect, total: question.dropdowns.length }
	}
}

/**
 * fakeObjectId - this is a helper function for allowing a string to be used as an objectId to help with typings
 *
 * @param  {string} str - the string to convert
 *
 * @returns ObjectID - the resulting objectId
 */
export function fakeObjectId(str: string): never {
	return str as never
}
